import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Calendar, MessageSquare, CheckCircle, AlertCircle, Building2, LogOut, UserPlus, X, Check, ArrowLeft, Users, MessageCircle, ShieldX } from 'lucide-react';
import { toast } from 'sonner';
import { clientTeamService, type ClientTeamMember, type ClientTeamInvitation } from '@/services/clientTeamService';
import { useClientPortal } from '@/hooks/useClientPortal';
import { FeedbackThread } from '@/components/FeedbackThread';
import { supabase } from '@/integrations/supabase/client';
import { ClientPortal } from '@/components/ClientPortal';

export const ClientDashboard: React.FC = () => {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState<ClientTeamMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<ClientTeamInvitation[]>([]);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [acceptingInvite, setAcceptingInvite] = useState<string | null>(null);
  const [deactivatedMembership, setDeactivatedMembership] = useState<ClientTeamMember | null>(null);
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const [pendingInvitationFromStorage, setPendingInvitationFromStorage] = useState<ClientTeamInvitation | null>(null);
  
  // CRITICAL: Synchronous redirect for pending client portal invite
  // This MUST happen BEFORE any other rendering to prevent "No Client Access" flash
  const [redirectingToInvite, setRedirectingToInvite] = useState(true); // Start as true to block rendering

  // Use client portal hook for the selected client
  const clientPortalData = useClientPortal(selectedClient || '');

  // Get current membership for selected client
  const currentMembership = memberships.find(m => m.client_id === selectedClient);

  // CRITICAL: Use useLayoutEffect for synchronous redirect check BEFORE paint
  // This runs synchronously after DOM mutations but before the browser paints
  useLayoutEffect(() => {
    const storedClientId = localStorage.getItem('pending_client_portal_invite');
    if (storedClientId) {
      // DO NOT remove token here - let ClientPortalInvitePage handle it after user accepts/declines
      // This ensures the token persists through the redirect and is available when needed
      navigate(`/client-portal-invite/${storedClientId}`, { replace: true });
      // Keep redirectingToInvite true - we're navigating away
      return;
    }
    // No pending invite, allow normal rendering
    setRedirectingToInvite(false);
  }, [navigate]);

  // Check for team member invitations (separate from client portal invites)
  useEffect(() => {
    const checkTeamInvitation = async () => {
      if (!user?.email || redirectingToInvite) return;
      
      // Check for team member invitation
      const storedTeamToken = localStorage.getItem('pending_team_invitation_token');
      if (storedTeamToken) {
        try {
          const invitation = await clientTeamService.getInvitationByToken(storedTeamToken);
          if (invitation) {
            setPendingInvitationFromStorage(invitation);
            setShowInvitationDialog(true);
            return;
          } else {
            localStorage.removeItem('pending_team_invitation_token');
          }
        } catch (error) {
          console.error('Error loading stored team invitation:', error);
          localStorage.removeItem('pending_team_invitation_token');
        }
      }
      
      // Also check for client portal session (old format)
      const storedSession = localStorage.getItem('pending_client_portal_session');
      if (storedSession) {
        try {
          localStorage.removeItem('pending_client_portal_session');
          navigate(`/client-portal/${storedSession}`, { replace: true });
        } catch (error) {
          console.error('Error handling client portal session:', error);
          localStorage.removeItem('pending_client_portal_session');
        }
      }
    };

    if (user && !authLoading && !redirectingToInvite) {
      checkTeamInvitation();
    }
  }, [user, authLoading, navigate, redirectingToInvite]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate('/auth');
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load all memberships (including inactive to check deactivation)
        const { data: allMemberships, error: membershipsError } = await supabase
          .from('client_team_members')
          .select(`
            *,
            clients:client_id (
              name,
              company
            )
          `)
          .eq('user_id', user.id);

        if (membershipsError) throw membershipsError;

        const activeMemberships = (allMemberships || []).filter(m => m.is_active);
        const inactiveMemberships = (allMemberships || []).filter(m => !m.is_active);

        setMemberships(activeMemberships);
        
        // Check if user was deactivated
        if (inactiveMemberships.length > 0 && activeMemberships.length === 0) {
          setDeactivatedMembership(inactiveMemberships[0]);
        }
        
        // Load pending invitations for this user's email
        if (user.email) {
          const invitations = await clientTeamService.getPendingInvitationsForEmail(user.email);
          setPendingInvitations(invitations);
        }
        
        // Auto-select first client if only one
        if (activeMemberships.length === 1) {
          setSelectedClient(activeMemberships[0].client_id);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, authLoading, navigate]);

  // Real-time subscription for membership changes
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('membership-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_team_members',
          filter: `user_id=eq.${user.id}`
        },
        async () => {
          // Reload memberships
          const { data: allMemberships } = await supabase
            .from('client_team_members')
            .select(`
              *,
              clients:client_id (
                name,
                company
              )
            `)
            .eq('user_id', user.id);

          const activeMemberships = (allMemberships || []).filter(m => m.is_active);
          const inactiveMemberships = (allMemberships || []).filter(m => !m.is_active);

          setMemberships(activeMemberships);
          
          // Check if user was deactivated
          if (inactiveMemberships.length > 0 && activeMemberships.length === 0) {
            setDeactivatedMembership(inactiveMemberships[0]);
            setSelectedClient(null);
          } else {
            setDeactivatedMembership(null);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleAcceptInvitation = async (invitation: ClientTeamInvitation) => {
    if (!user) return;
    setAcceptingInvite(invitation.id);
    try {
      await clientTeamService.acceptInvitation(invitation.invitation_token, user.id);
      localStorage.removeItem('pending_team_invitation_token');
      toast.success('Invitation accepted! You now have access to this client.');
      
      // Refresh data
      const userMemberships = await clientTeamService.getCurrentUserMemberships();
      setMemberships(userMemberships);
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
      setPendingInvitationFromStorage(null);
      setShowInvitationDialog(false);
      
      if (userMemberships.length === 1) {
        setSelectedClient(userMemberships[0].client_id);
      }
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      toast.error(error.message || 'Failed to accept invitation');
    } finally {
      setAcceptingInvite(null);
    }
  };

  const handleDeclineInvitation = async (invitation: ClientTeamInvitation) => {
    try {
      await clientTeamService.declineInvitation(invitation.invitation_token);
      localStorage.removeItem('pending_team_invitation_token');
      toast.success('Invitation declined');
      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
      setPendingInvitationFromStorage(null);
      setShowInvitationDialog(false);
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast.error('Failed to decline invitation');
    }
  };

  const handleStoredInvitationAccept = async () => {
    if (!pendingInvitationFromStorage || !user) return;
    await handleAcceptInvitation(pendingInvitationFromStorage);
  };

  const handleStoredInvitationDecline = async () => {
    if (!pendingInvitationFromStorage) return;
    await handleDeclineInvitation(pendingInvitationFromStorage);
  };

  if (authLoading || loading || redirectingToInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Show deactivated message
  if (deactivatedMembership) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center justify-center p-8 text-center">
            <div className="bg-destructive/10 p-4 rounded-full mb-4">
              <ShieldX className="h-12 w-12 text-destructive" />
            </div>
            <h2 className="text-lg font-semibold mb-2">Access Removed</h2>
            <p className="text-muted-foreground mb-4">
              You have been removed as a team member by the client/event planner. 
              Please contact them if you believe this is an error.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="flex-1">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <Button onClick={handleSignOut} variant="outline" className="flex-1">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (memberships.length === 0 && pendingInvitations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center justify-center p-8 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold mb-2">No Client Access</h2>
            <p className="text-muted-foreground mb-4">
              You don't have access to any client portals yet. Please contact your administrator.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="flex-1">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <Button onClick={handleSignOut} variant="outline" className="flex-1">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // FIX ISSUE 2: If user is a CLIENT (role='client'), render the dedicated ClientPortal component
  // This restores the original client-specific UI that was regressed
  const clientMembership = memberships.find((m: any) => m.role === 'client');
  if (clientMembership) {
    return <ClientPortal clientId={clientMembership.client_id} />;
  }

  // Otherwise, show the Team Portal UI for team members (unchanged)
  return (
    <div className="min-h-screen bg-muted/50">
      {/* Invitation Dialog - Shows automatically after login */}
      <Dialog open={showInvitationDialog} onOpenChange={setShowInvitationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <img 
                src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" 
                alt="Event Date Picker Logo" 
                className="h-10 w-auto" 
              />
            </div>
            <DialogTitle className="text-center">Team Member Invitation</DialogTitle>
          </DialogHeader>
          {pendingInvitationFromStorage && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Client</span>
                  <span className="font-semibold">{pendingInvitationFromStorage.clients?.name || 'Client'}</span>
                </div>
                {pendingInvitationFromStorage.clients?.company && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Company</span>
                    <span className="font-medium">{pendingInvitationFromStorage.clients.company}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Your Role</span>
                  <Badge variant="secondary" className="capitalize">{pendingInvitationFromStorage.role}</Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground text-center">
                By accepting, you'll be able to view events and feedback for this client.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleStoredInvitationDecline}
                  disabled={acceptingInvite === pendingInvitationFromStorage.id}
                >
                  <X className="h-4 w-4 mr-2" />
                  Decline
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleStoredInvitationAccept}
                  disabled={acceptingInvite === pendingInvitationFromStorage.id}
                >
                  {acceptingInvite === pendingInvitationFromStorage.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Accept
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="border-b bg-background">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {/* Logo */}
              <img 
                src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" 
                alt="Event Date Picker Logo" 
                className="h-10 w-auto" 
              />
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Show portal type based on membership role */}
                  {memberships.some((m: any) => m.role === 'client') ? (
                    <>
                      <h1 className="text-xl sm:text-2xl font-bold">Client Portal</h1>
                      <Badge variant="default">Client</Badge>
                    </>
                  ) : (
                    <>
                      <h1 className="text-xl sm:text-2xl font-bold">Team Portal</h1>
                      <Badge variant="secondary">Team Member</Badge>
                    </>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">Welcome, {user?.user_metadata?.display_name || user?.email}</p>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="flex-1 sm:flex-initial">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <Button onClick={handleSignOut} variant="outline" size="sm" className="flex-1 sm:flex-initial">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 sm:py-8">
        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <Card className="mb-6 border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserPlus className="h-5 w-5 text-primary" />
                Pending Invitations ({pendingInvitations.length})
              </CardTitle>
              <CardDescription>You have pending invitations to join client teams</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingInvitations.map((invitation) => (
                  <Card key={invitation.id} className="border">
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <h3 className="font-semibold">{invitation.clients?.name || 'Client'}</h3>
                          {invitation.clients?.company && (
                            <p className="text-sm text-muted-foreground">{invitation.clients.company}</p>
                          )}
                          <Badge variant="secondary" className="mt-1 capitalize">{invitation.role}</Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAcceptInvitation(invitation)}
                            disabled={acceptingInvite === invitation.id}
                          >
                            {acceptingInvite === invitation.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <Check className="h-4 w-4 mr-1" />
                            )}
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeclineInvitation(invitation)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Decline
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Client Selection */}
        {memberships.length > 1 && (
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle>Select Client</CardTitle>
              <CardDescription>Choose which client portal to access</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {memberships.map((membership: any) => (
                  <Card 
                    key={membership.id}
                    className={`cursor-pointer transition-colors ${
                      selectedClient === membership.client_id 
                        ? 'border-primary bg-primary/5' 
                        : 'hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedClient(membership.client_id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold truncate">{membership.clients?.name}</h3>
                          {membership.clients?.company && (
                            <p className="text-sm text-muted-foreground truncate">{membership.clients.company}</p>
                          )}
                        </div>
                        <Badge variant={membership.role === 'admin' ? 'destructive' : 'secondary'} className="capitalize flex-shrink-0">
                          {membership.role}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Portal Content */}
        {selectedClient ? (
          <TeamMemberPortalContent 
            clientId={selectedClient}
            clientPortalData={clientPortalData}
            memberRole={currentMembership?.role || 'member'}
          />
        ) : memberships.length === 1 ? null : (
          <Card>
            <CardContent className="flex items-center justify-center p-8">
              <p className="text-muted-foreground">Please select a client to view their portal</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Team Member Portal Content Component (read-only permissions with feedback reply)
interface TeamMemberPortalContentProps {
  clientId: string;
  clientPortalData: any;
  memberRole: string;
}

const TeamMemberPortalContent: React.FC<TeamMemberPortalContentProps> = ({
  clientId,
  clientPortalData,
  memberRole
}) => {
  const { user } = useAuth();
  const { events, approvals, feedback, loading } = clientPortalData;
  const [selectedFeedback, setSelectedFeedback] = useState<any>(null);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading portal data...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Tabs defaultValue="events" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="events" className="text-xs sm:text-sm">Events ({events.length})</TabsTrigger>
          <TabsTrigger value="feedback" className="text-xs sm:text-sm">Feedback ({feedback.length})</TabsTrigger>
          <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle>Events</CardTitle>
              <CardDescription>View events for this client</CardDescription>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p>No events found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {events.map((event: any) => (
                    <Card key={event.id} className="border-l-4 border-l-primary">
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold truncate">{event.name}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                              <span>Host: {event.host_name}</span>
                              <span>Created: {new Date(event.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <Badge variant="outline" className="flex-shrink-0">View Only</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="feedback">
          <Card>
            <CardHeader>
              <CardTitle>Feedback History</CardTitle>
              <CardDescription>View and reply to feedback conversations</CardDescription>
            </CardHeader>
            <CardContent>
              {feedback.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p>No feedback submitted</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {feedback.map((item: any) => (
                    <Card 
                      key={item.id} 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => setSelectedFeedback(item)}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant="outline">{item.feedback_type}</Badge>
                              <Badge 
                                variant={
                                  item.priority === 'urgent' ? 'destructive' :
                                  item.priority === 'high' ? 'default' : 'secondary'
                                }
                              >
                                {item.priority}
                              </Badge>
                            </div>
                            <p className="text-sm mb-2 line-clamp-2">{item.message}</p>
                            <p className="text-xs text-muted-foreground">
                              Submitted: {new Date(item.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge 
                              variant={
                                item.status === 'addressed' ? 'default' :
                                item.status === 'closed' ? 'secondary' : 'outline'
                              }
                            >
                              {item.status}
                            </Badge>
                            <Button variant="ghost" size="sm">
                              <MessageCircle className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">View</span>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div className="ml-2">
                    <p className="text-xs sm:text-sm font-medium text-muted-foreground">Total Events</p>
                    <p className="text-xl sm:text-2xl font-bold">{events.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                  <div className="ml-2">
                    <p className="text-xs sm:text-sm font-medium text-muted-foreground">Pending</p>
                    <p className="text-xl sm:text-2xl font-bold">
                      {approvals.filter((a: any) => a.status === 'pending').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <div className="ml-2">
                    <p className="text-xs sm:text-sm font-medium text-muted-foreground">Open Feedback</p>
                    <p className="text-xl sm:text-2xl font-bold">
                      {feedback.filter((f: any) => f.status === 'open').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  <div className="ml-2">
                    <p className="text-xs sm:text-sm font-medium text-muted-foreground">Approved</p>
                    <p className="text-xl sm:text-2xl font-bold">
                      {approvals.filter((a: any) => a.status === 'approved').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Feedback Thread Dialog for Team Members */}
      <Dialog open={!!selectedFeedback} onOpenChange={(open) => !open && setSelectedFeedback(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] mx-4 sm:mx-auto">
          {selectedFeedback && (
            <FeedbackThread
              feedbackId={selectedFeedback.id}
              feedbackMessage={selectedFeedback.message}
              feedbackType={selectedFeedback.feedback_type}
              feedbackPriority={selectedFeedback.priority}
              feedbackStatus={selectedFeedback.status}
              eventName={events.find((e: any) => e.id === selectedFeedback.event_id)?.name || 'Event'}
              userRole="team_member"
              userName={user?.user_metadata?.display_name || user?.email?.split('@')[0]}
              authorName="Client"
              createdAt={selectedFeedback.created_at}
              canReply={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
